<template>
<div class="sd-root" :style="themePageStyle">

  <nav class="sd-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/sell/products')"><i class="mdi mdi-arrow-left" style="color:var(--fm-m,#A09888);font-size:22px;"></i></a>
      <a href="javascript:void(0)" @click="$router.push('/dashboard')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:30px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Delivery Settings</span>
    </div>
    <button class="btn btn-sm btn-outline-warning" @click="$router.push('/sell/products')">My Products</button>
  </div></nav>

  <div class="container sd-container">

    <div class="mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">🚚 Your Delivery Rates</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;">
        You deliver agro-input orders directly to buyers — Flockr is not involved in transport.
        Set your pickup point and rate card here. The system calculates each buyer's delivery fee from the
        straight-line distance to their address.
      </p>
    </div>

    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <form v-else @submit.prevent="save">

      <!-- Pickup point -->
      <div class="sd-card">
        <h6 class="sd-section"><i class="mdi mdi-map-marker me-1" style="color:#BFA164;"></i>Pickup Point</h6>
        <p style="color:var(--fm-s,#6B6356);font-size:11px;">Where buyers collect from (or where deliveries start). Distance is measured from here.</p>

        <label class="sd-lbl">Address <span style="color:#FF6B6B;">*</span></label>
        <div style="position:relative;">
          <input v-model="form.pickupAddress" class="form-control sd-input" placeholder="Search town, market, area..." @input="onAddrSearch" @focus="showLocResults=true" autocomplete="off" />
          <button type="button" class="btn btn-sm btn-link" style="position:absolute;right:6px;top:6px;" @click="useGps" :disabled="gpsBusy" :title="gpsBusy ? 'Detecting...' : 'Use my GPS'">
            <span v-if="gpsBusy" class="spinner-border spinner-border-sm"></span>
            <i v-else class="mdi mdi-crosshairs-gps" style="color:#BFA164;font-size:18px;"></i>
          </button>
          <div v-if="showLocResults && locResults.length" class="sd-loc-list">
            <div v-for="(r, ri) in locResults" :key="ri" class="sd-loc-item" @click="pickLocation(r)">
              <i class="mdi mdi-map-marker me-2" style="color:#BFA164;"></i>{{ r.display_name }}
            </div>
          </div>
        </div>
        <div v-if="form.pickupLat && form.pickupLng" style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:4px;">
          📍 {{ Number(form.pickupLat).toFixed(5) }}, {{ Number(form.pickupLng).toFixed(5) }}
        </div>
      </div>

      <!-- Rate card -->
      <div class="sd-card">
        <h6 class="sd-section"><i class="mdi mdi-cash me-1" style="color:#BFA164;"></i>Rate Card</h6>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="sd-lbl">Free delivery radius (km)</label>
            <input type="number" step="0.1" v-model.number="form.freeRadiusKm" class="form-control sd-input" placeholder="0" />
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">Buyers within this distance pay no per-km fee — only the base fee.</div>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Base delivery fee (KES)</label>
            <input type="number" v-model.number="form.baseFeeKes" class="form-control sd-input" placeholder="200" />
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">Flat fee added to every delivery (covers fuel, time, packaging).</div>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Per-km rate (KES/km)</label>
            <input type="number" step="0.5" v-model.number="form.perKmKes" class="form-control sd-input" placeholder="50" />
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">Charged for every km beyond the free radius.</div>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Max delivery distance (km) <span style="color:#FF6B6B;">*</span></label>
            <input type="number" step="0.5" v-model.number="form.maxRadiusKm" class="form-control sd-input" placeholder="50" />
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">Buyers beyond this can still order if they pick up — but you won't deliver further.</div>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Allow free pickup at your shop?</label>
            <select v-model.number="form.allowPickup" class="form-select sd-input">
              <option :value="1">Yes — buyers can collect for free</option>
              <option :value="0">No — only delivery</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Lead time (hours)</label>
            <input type="number" v-model.number="form.leadTimeHours" class="form-control sd-input" />
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">How long you need to prepare an order before delivery.</div>
          </div>
          <div class="col-12">
            <label class="sd-lbl">Notes for buyers (optional)</label>
            <textarea v-model="form.notes" rows="2" class="form-control sd-input" maxlength="500" placeholder="e.g., Free delivery within Kisumu CBD on orders over KES 5,000. Same-day delivery if ordered before 2 PM."></textarea>
          </div>
          <div class="col-md-6">
            <label class="sd-lbl">Status</label>
            <select v-model.number="form.isActive" class="form-select sd-input">
              <option :value="1">Active — accepting orders</option>
              <option :value="0">Paused — temporarily not delivering</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Live preview -->
      <div v-if="form.pickupLat && form.pickupLng" class="sd-card">
        <h6 class="sd-section"><i class="mdi mdi-calculator me-1" style="color:#BFA164;"></i>What buyers will pay</h6>
        <p style="color:var(--fm-s,#6B6356);font-size:11px;">Buyer's distance from your pickup point determines the fee.</p>
        <table class="sd-preview-table">
          <thead><tr><th>Distance</th><th>Delivery fee</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="d in [0, 2, 5, 10, 20, 35, 50, 75]" :key="d">
              <td>{{ d }} km</td>
              <td>{{ previewFee(d) }}</td>
              <td>{{ previewStatus(d) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="error" class="text-danger" style="font-size:13px;">{{ error }}</div>
      <div v-if="success" class="text-success" style="font-size:13px;">✓ Saved — buyers will see the new rates immediately.</div>

      <div class="d-flex gap-2 mt-3">
        <button type="button" class="btn btn-outline-secondary" @click="$router.push('/sell/products')" :disabled="saving">Back</button>
        <button type="submit" class="btn btn-warning flex-fill" :disabled="saving || !canSave">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="mdi mdi-content-save me-1"></i>
          {{ saving ? 'Saving...' : 'Save Delivery Settings' }}
        </button>
      </div>
    </form>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .sd-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .sd-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .sd-card,
html[data-theme="light"] .sd-loc-list{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .sd-input{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
</style>

<style scoped>
.sd-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.sd-topbar{position:sticky;top:0;z-index:100;padding:10px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.sd-container{padding-top:80px;padding-bottom:60px;max-width:760px}
.sd-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px 20px;margin-bottom:14px}
.sd-section{color:var(--fm-h,#F5F0E8);margin-bottom:12px}
.sd-lbl{display:block;color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin-bottom:6px;margin-top:8px}
.sd-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.sd-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.sd-loc-list{position:absolute;top:100%;left:0;right:0;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;max-height:240px;overflow-y:auto;z-index:50;margin-top:2px}
.sd-loc-item{padding:8px 12px;cursor:pointer;font-size:12px;color:var(--fm-h,#F5F0E8);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.sd-loc-item:hover{background:rgba(191,161,100,.08);color:#BFA164}
.sd-preview-table{width:100%;font-size:13px}
.sd-preview-table th{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.4px;padding:6px 10px;text-align:left;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.sd-preview-table td{padding:6px 10px;color:var(--fm-h,#F5F0E8);border-bottom:1px solid var(--fm-br,#1A1A1A)}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";

export default {
  name: "SellerDeliverySettings",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,saving:false,
    form:{ pickupAddress:'', pickupLat:null, pickupLng:null, freeRadiusKm:0, baseFeeKes:200, perKmKes:50, maxRadiusKm:50, allowPickup:1, leadTimeHours:24, notes:'', isActive:1 },
    locResults:[], showLocResults:false, locSearchTimer:null, gpsBusy:false,
    error:'', success:false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    canSave(){return !!(this.form.pickupAddress && this.form.pickupLat && this.form.pickupLng && this.form.maxRadiusKm > 0)}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    if(!sessionStorage.getItem('userId')){this.$router.push('/login?redirect=/sell/delivery');return}
    this.load();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}if(this.locSearchTimer)clearTimeout(this.locSearchTimer)},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    fNum(n){return Number(n||0).toLocaleString()},
    previewFee(km){
      var f=this.form;
      if (km > Number(f.maxRadiusKm||0)) return '—';
      var billable = Math.max(0, km - Number(f.freeRadiusKm||0));
      var fee = Math.round(Number(f.baseFeeKes||0) + billable * Number(f.perKmKes||0));
      return 'KES ' + this.fNum(fee);
    },
    previewStatus(km){
      var f=this.form;
      if (km > Number(f.maxRadiusKm||0)) return f.allowPickup === 1 ? 'Pickup only' : 'Out of range';
      if (km <= Number(f.freeRadiusKm||0)) return 'Within free radius';
      return 'Standard delivery';
    },
    onAddrSearch(){
      var self=this;
      var q=String(this.form.pickupAddress||'').trim();
      if (this.locSearchTimer) clearTimeout(this.locSearchTimer);
      if (q.length < 3) { this.locResults=[]; return; }
      this.locSearchTimer = setTimeout(async function(){
        try {
          var resp = await fetch('https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke,tz,ug&limit=6&q=' + encodeURIComponent(q), { headers:{ 'Accept-Language':'en' } });
          self.locResults = await resp.json();
          self.showLocResults = true;
        } catch(e) {}
      }, 400);
    },
    pickLocation(loc){
      this.form.pickupAddress = loc.display_name;
      this.form.pickupLat = parseFloat(loc.lat);
      this.form.pickupLng = parseFloat(loc.lon);
      this.locResults=[]; this.showLocResults=false;
    },
    useGps(){
      var self=this;
      if (!navigator.geolocation) { alert('GPS not available on this browser'); return; }
      this.gpsBusy=true;
      navigator.geolocation.getCurrentPosition(async function(pos){
        self.form.pickupLat = pos.coords.latitude;
        self.form.pickupLng = pos.coords.longitude;
        try {
          var url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + self.form.pickupLat + '&lon=' + self.form.pickupLng + '&zoom=17';
          var resp = await fetch(url, { headers:{'Accept-Language':'en'} });
          var data = await resp.json();
          if (data && data.display_name) self.form.pickupAddress = data.display_name;
        } catch(e){}
        self.gpsBusy=false;
      }, function(){ self.gpsBusy=false; alert('Could not get GPS — check permissions'); }, { timeout: 8000 });
    },
    async load(){
      this.loading=true;
      try{
        var r=await executeJson({function:'getSellerDeliverySettings',sellerId:sessionStorage.getItem('userId')},flocker);
        if(r.data&&r.data.success&&r.data.data&&r.data.data.settings){
          var s=r.data.data.settings;
          this.form={
            pickupAddress: s.pickup_address || '',
            pickupLat: s.pickup_lat ? Number(s.pickup_lat) : null,
            pickupLng: s.pickup_lng ? Number(s.pickup_lng) : null,
            freeRadiusKm: Number(s.free_radius_km || 0),
            baseFeeKes: Number(s.base_fee_kes || 0),
            perKmKes: Number(s.per_km_kes || 0),
            maxRadiusKm: Number(s.max_radius_km || 50),
            allowPickup: Number(s.allow_pickup || 0),
            leadTimeHours: Number(s.lead_time_hours || 24),
            notes: s.notes || '',
            isActive: Number(s.is_active || 1)
          };
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async save(){
      this.saving=true; this.error=''; this.success=false;
      try{
        var f=this.form;
        var r=await executeJson({
          function:'upsertSellerDeliverySettings',
          userId: sessionStorage.getItem('userId'),
          pickupAddress: f.pickupAddress, pickupLat: f.pickupLat, pickupLng: f.pickupLng,
          freeRadiusKm: f.freeRadiusKm, baseFeeKes: f.baseFeeKes, perKmKes: f.perKmKes,
          maxRadiusKm: f.maxRadiusKm, allowPickup: f.allowPickup, leadTimeHours: f.leadTimeHours,
          notes: f.notes, isActive: f.isActive
        },flocker);
        if(r.data&&r.data.success){this.success=true;setTimeout(()=>{this.success=false},3500)}
        else this.error=(r.data&&r.data.message)||'Could not save';
      }catch(e){this.error=(e&&e.message)||'Network error'}
      this.saving=false;
    }
  }
};
</script>
